import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
  Mail,
  MailOpen,
  Activity
} from 'lucide-react';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Loading';

import {
  emailLogService,
  EmailLogDto,
  EmailLogFilters,
  EmailLogStatsDto,
  EmailDirection,
  EmailStatus,
  EmailType,
  PaginatedResponse
} from '@/services/emailLogService';

export const EmailLogs: React.FC = () => {
  // State
  const [emailLogs, setEmailLogs] = useState<EmailLogDto[]>([]);
  const [stats, setStats] = useState<EmailLogStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<EmailLogDto | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filters, setFilters] = useState<EmailLogFilters>({
    page: 1,
    pageSize: 20
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load data
  const loadEmailLogs = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Apply search term to filters
      const searchFilters = { ...filters };
      if (searchTerm.trim()) {
        if (searchTerm.includes('@')) {
          searchFilters.fromEmail = searchTerm.trim();
        } else {
          // Could be ticket number or other search
          searchFilters.ticketId = searchTerm.trim();
        }
      }

      const [logsResponse, statsResponse] = await Promise.all([
        emailLogService.getEmailLogs(searchFilters),
        emailLogService.getEmailLogStatistics(filters.startDate, filters.endDate)
      ]);

      setEmailLogs(logsResponse.data);
      setTotalCount(logsResponse.totalCount);
      setTotalPages(logsResponse.totalPages);
      setCurrentPage(logsResponse.pageNumber);
      setStats(statsResponse);
    } catch (err) {
      setError('Failed to load email logs');
      console.error('Error loading email logs:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, searchTerm]);

  // Initial load
  useEffect(() => {
    loadEmailLogs();
  }, [loadEmailLogs]);

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<EmailLogFilters>) => {
    const updatedFilters = {
      ...filters,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    };
    setFilters(updatedFilters);
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    const updatedFilters = { ...filters, page };
    setFilters(updatedFilters);
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadEmailLogs(true);
  };

  // Handle export
  const handleExport = async () => {
    try {
      const blob = await emailLogService.exportEmailLogs(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-logs-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export email logs');
      console.error('Error exporting email logs:', err);
    }
  };

  // View log details
  const viewLogDetails = (log: EmailLogDto) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Get status icon
  const getStatusIcon = (status: EmailStatus) => {
    switch (status) {
      case EmailStatus.Success:
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case EmailStatus.Failed:
        return <XCircle className="w-5 h-5 text-red-600" />;
      case EmailStatus.Spam:
        return <Shield className="w-5 h-5 text-yellow-600" />;
      case EmailStatus.Blocked:
        return <Shield className="w-5 h-5 text-red-600" />;
      case EmailStatus.Pending:
        return <Clock className="w-5 h-5 text-gray-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Logs</h2>
          <p className="text-gray-600">Monitor inbound and outbound email processing</p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            className="flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Emails</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalEmails}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.totalEmails > 0 ? ((stats.successfulEmails / stats.totalEmails) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed Emails</p>
                <p className="text-2xl font-bold text-red-600">{stats.failedEmails}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Processing Time</p>
                <p className="text-2xl font-bold text-blue-600">
                  {emailLogService.formatProcessingTime(stats.averageProcessingTimeMs)}
                </p>
              </div>
              <Activity className="w-8 h-8 text-blue-600" />
            </div>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by email address or ticket ID..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Direction
                </label>
                <select
                  value={filters.direction || ''}
                  onChange={(e) => handleFilterChange({ direction: e.target.value as EmailDirection || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Directions</option>
                  <option value={EmailDirection.Inbound}>Inbound</option>
                  <option value={EmailDirection.Outbound}>Outbound</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange({ status: e.target.value as EmailStatus || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value={EmailStatus.Success}>Success</option>
                  <option value={EmailStatus.Failed}>Failed</option>
                  <option value={EmailStatus.Spam}>Spam</option>
                  <option value={EmailStatus.Blocked}>Blocked</option>
                  <option value={EmailStatus.Pending}>Pending</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => handleFilterChange({ startDate: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => handleFilterChange({ endDate: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Error Message */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </Card>
      )}

      {/* Email Logs Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Direction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Processing Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {emailLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(log.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${emailLogService.getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${emailLogService.getDirectionColor(log.direction)}`}>
                      {log.direction}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {log.direction === EmailDirection.Inbound ? `From: ${log.fromEmail}` : `To: ${log.toEmails}`}
                      </div>
                      {log.direction === EmailDirection.Inbound && (
                        <div className="text-gray-500">To: {log.toEmails}</div>
                      )}
                      {log.ticketNumber && (
                        <div className="text-blue-600">Ticket: {log.ticketNumber}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate" title={log.subject}>
                      {log.subject}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {emailLogService.formatProcessingTime(log.processingTimeMs)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(log.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewLogDetails(log)}
                      className="flex items-center space-x-1"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  aria-label="Previous page"
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  aria-label="Next page"
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Log Details Modal */}
      {showDetails && selectedLog && (
        <EmailLogDetailsModal
          log={selectedLog}
          onClose={() => setShowDetails(false)}
        />
      )}
    </div>
  );
};

// Email Log Details Modal Component
interface EmailLogDetailsModalProps {
  log: EmailLogDto;
  onClose: () => void;
}

const EmailLogDetailsModal: React.FC<EmailLogDetailsModalProps> = ({ log, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Email Log Details</h2>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>

          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${emailLogService.getStatusColor(log.status)}`}>
                    {log.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Direction</label>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${emailLogService.getDirectionColor(log.direction)}`}>
                    {log.direction}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <p className="text-gray-900">{log.type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Endpoint</label>
                  <p className="text-gray-900 font-mono text-sm">{log.endpoint}</p>
                </div>
              </div>
            </div>

            {/* Email Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Email Details</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">From</label>
                  <p className="text-gray-900">{log.fromEmail} {log.fromName && `(${log.fromName})`}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">To</label>
                  <p className="text-gray-900">{log.toEmails}</p>
                </div>
                {log.ccEmails && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CC</label>
                    <p className="text-gray-900">{log.ccEmails}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <p className="text-gray-900">{log.subject}</p>
                </div>
              </div>
            </div>

            {/* Processing Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Processing Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Processing Time</label>
                  <p className="text-gray-900">{emailLogService.formatProcessingTime(log.processingTimeMs)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IP Address</label>
                  <p className="text-gray-900">{log.ipAddress || 'N/A'}</p>
                </div>
                {log.ticketNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ticket</label>
                    <p className="text-gray-900">{log.ticketNumber}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created At</label>
                  <p className="text-gray-900">{new Date(log.createdAt).toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Error Details */}
            {log.errorDetails && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Error Details</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{log.errorDetails}</p>
                </div>
              </div>
            )}

            {/* Metadata */}
            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Metadata</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                    {JSON.stringify(log.metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};